import { z } from "zod";

import {
  ChatRetentionActionSchema,
  RETENTION_POLICY_DAYS_MAX,
  RETENTION_POLICY_DAYS_MIN
} from "../../deployments/schemas/retention-policy.schema";

/**
 * One organization's chat-retention policy: what the daily retention sweep does
 * to that organization's conversations once they have been inactive for `days`
 * days.
 *
 * The row is `OrganizationRetentionPolicy`, at most one per organization. It is
 * the BASE of a two-scope hierarchy — `DeploymentRetentionPolicy` is the
 * per-deployment OVERRIDE — and it exists because a policy keyed on a deployment
 * can only ever reach a conversation that HAS one. Playground, internal, cron and
 * API chats, and any chat whose deployment was hard-deleted, sit outside every
 * per-deployment policy by construction (migration
 * `20260916120000_add_organization_retention_policy`).
 *
 * An organization with no row has no policy, and reads as
 * {@link ORGANIZATION_RETENTION_POLICY_DEFAULTS} with `configured: false` — never
 * as a 404, because "never configured" is the ordinary state of every
 * organization rather than an error.
 *
 * THE ACTION VOCABULARY AND THE DAYS BOUND ARE IMPORTED, NOT RESTATED. One
 * `ChatRetentionAction` serves both scopes so the effective-policy resolver reads
 * a single type, and one 1..3650 bound keeps the two scopes structurally
 * identical inputs to it. A second copy of either would be two catalogues free to
 * disagree.
 *
 * They are deliberately NOT re-exported from here. `packages/types/src/api/index.ts`
 * already publishes them off the deployments domain, and a second export path for
 * one symbol is an ambiguity the barrel resolves silently — one home, one name.
 */

/**
 * The body of `PUT /api/organizations/me/retention-policy`.
 *
 * - `enabled` and `days` are always sent: they are the policy.
 * - `action` is required once the policy is enabled. A DISABLED write may omit
 *   it, and then keeps the action already stored — but an organization with no
 *   policy yet has nothing to keep, so its first write names one (the server
 *   answers `RETENTION_POLICY_ACTION_REQUIRED` otherwise).
 * - The three behaviour flags are optional. Omitted on a first write they take
 *   the database defaults; omitted on a later write they keep the stored value.
 *
 * There is no `organizationId` here and an unknown key is stripped, not
 * honoured: the organization IS the caller's session. That is the whole reason
 * this route carries no path variable where its per-deployment sibling carries
 * `:id` — there is no organization to select, so there is nothing a request could
 * name and nothing for the server to check a name against.
 */
export const UpsertOrganizationRetentionPolicyBodySchema = z
  .object({
    enabled: z.boolean(),
    action: ChatRetentionActionSchema.optional(),
    days: z.number().int().min(RETENTION_POLICY_DAYS_MIN).max(RETENTION_POLICY_DAYS_MAX),
    fireStatusEvents: z.boolean().optional(),
    skipOpenTickets: z.boolean().optional(),
    skipAssigned: z.boolean().optional()
  })
  .superRefine((body, ctx) => {
    if (body.enabled && body.action === undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["action"],
        message: "An enabled retention policy needs an action: ARCHIVE, DELETE or SHRED."
      });
    }
  });

export type UpsertOrganizationRetentionPolicyBody = z.infer<
  typeof UpsertOrganizationRetentionPolicyBodySchema
>;

/**
 * An organization's policy as the API returns it — the stored row, or the
 * defaults when none is stored.
 *
 * `configured` separates the two, because the defaults are a legitimate stored
 * value as well: a row saved as `enabled: false` with the default flags would
 * otherwise read exactly like an organization nobody has configured. `action` and
 * `days` are `null` only while `configured` is false — the table carries no
 * default for either.
 */
export const OrganizationRetentionPolicyResponseSchema = z.object({
  organizationId: z.string(),
  configured: z.boolean(),
  enabled: z.boolean(),
  action: ChatRetentionActionSchema.nullable(),
  days: z.number().int().nullable(),
  fireStatusEvents: z.boolean(),
  skipOpenTickets: z.boolean(),
  skipAssigned: z.boolean(),
  updatedAt: z.string().nullable()
});

export type OrganizationRetentionPolicyResponse = z.infer<
  typeof OrganizationRetentionPolicyResponseSchema
>;

/**
 * What an organization with no stored policy reads as.
 *
 * The four booleans are the column defaults of `OrganizationRetentionPolicy`
 * (migration `20260916120000_add_organization_retention_policy`), which is where
 * a default changes — never here first.
 *
 * 🔴 These are a SEPARATE constant from the per-deployment table's, and
 * deliberately so even though the two tables currently agree value-for-value.
 * They are the defaults of a DIFFERENT table: re-exporting the deployment
 * constant would make a later migration against one table silently move the other
 * scope's answer, with nothing to red.
 */
export const ORGANIZATION_RETENTION_POLICY_DEFAULTS = {
  enabled: false,
  action: null,
  days: null,
  fireStatusEvents: false,
  skipOpenTickets: true,
  skipAssigned: true
} as const satisfies Omit<
  OrganizationRetentionPolicyResponse,
  "organizationId" | "configured" | "updatedAt"
>;

/**
 * One deployment that replaces the organization's policy with its own.
 *
 * Every field here comes from a STORED `DeploymentRetentionPolicy` row, which is
 * why none of them is nullable where the organization's own response above has
 * three that are. That response has to describe an organization with NO row, so
 * `action`, `days` and `updatedAt` are `null` exactly while `configured` is
 * false. A row in this list IS the configuration — nothing unconfigured can
 * appear in it — and the table declares `action`, `days` and `updatedAt` NOT
 * NULL, so a nullable field here would be a state the database cannot produce and
 * a branch no reader could ever exercise.
 *
 * 🔴 `enabled: false` IS THE MOST IMPORTANT ROW IN THIS LIST, NOT A ROW TO FILTER
 * OUT. There is no delete on that table, so a row exists only because an admin
 * opened the deployment's tab and saved one; an off row is therefore a deliberate
 * EXEMPTION, and `resolveEffectiveRetentionPolicy` reads it as
 * `DEPLOYMENT_OPTED_OUT` and lets it suppress an enabled organization policy.
 * Serving only the enabled rows would report full coverage over the deployments
 * somebody just exempted — and under SHRED that reading is unrecoverable.
 *
 * `deploymentName` carries no `.min(1)`: it is the deployment's stored name
 * echoed back on a read, and refusing to serve a row because its name is empty
 * would hide an override rather than fix one.
 */
export const OrganizationRetentionOverrideSchema = z.object({
  deploymentId: z.string().uuid(),
  deploymentName: z.string(),
  enabled: z.boolean(),
  action: ChatRetentionActionSchema,
  days: z.number().int(),
  updatedAt: z.string()
});

export type OrganizationRetentionOverride = z.infer<typeof OrganizationRetentionOverrideSchema>;

/**
 * The body of `GET /api/organizations/me/retention-policy/overrides`.
 *
 * An object rather than a bare array, so a later field — a count, a truncation
 * marker — is an additive change instead of a breaking one.
 *
 * 🔴 THE LIST IS COMPLETE OR THE REQUEST FAILED; THERE IS NO PARTIAL STATE AND
 * NO `hidden` COUNT. The rows are not filtered by the caller's per-deployment
 * permission: the question this answers is "what escapes the organization
 * policy", and an override omitted from the answer re-creates the belief the
 * whole surface exists to prevent — that a policy covers conversations it does
 * not reach. So an empty array means exactly one thing, and a client may render
 * full coverage from it without a second field to consult.
 */
export const OrganizationRetentionOverridesResponseSchema = z.object({
  overrides: z.array(OrganizationRetentionOverrideSchema)
});

export type OrganizationRetentionOverridesResponse = z.infer<
  typeof OrganizationRetentionOverridesResponseSchema
>;
